import { useEffect, useRef, useState } from 'react';
import { MagnifyingGlass, Package } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * マスター品目ピッカー（入力欄＋候補一覧＋選択済み表示＋紐付け解除）。
 *
 * 資産を登録（EngAssetFormDialog）と 必要部品を追加（AddPartDialog）で**共有**する。
 * 同じ master-search を叩く画面が別々のUIを持っていたため、片方だけ直って
 * 食い違い続けていた（品番が truncate で消える等）。UIの殻もここに一本化する。
 *
 * 差分はプロパティで受ける:
 *   label / placeholder / helper : 文言
 *   sources                      : 残す source（既定 ['mfg','eng']）
 *   linked                       : 紐付け中の表示情報 {label, sku, sourceLabel} | null
 *   onUnlink                     : 明示的な解除。null なら解除UIを出さない
 *   onPick                       : 候補クリック
 *   renderResultExtra            : 候補行に足す表示（source バッジ等）
 *   statusSlot                   : ラベル横に出す任意のバッジ
 */
export const MasterItemPicker = ({
  label, placeholder = '品名・品番・図番・メーカー品番', helper,
  search, sources = ['mfg', 'eng'],
  // 製造品目の種別で絞る（材料ピッカー用）。既定は絞らない。
  // サーバ側にタイプ引数は無い（あの口は購買の所有）ので、既存の source 絞りと
  // 同じ場所でクライアント側だけで絞る。
  itemTypes = null,
  linked = null, onUnlink, onPick, onQueryChange,
  unlinkLabel = 'マスターの紐付けを外す',
  renderResultExtra, statusSlot, testIdPrefix,
  // 2文字から探す画面（登録フォーム）もあるので最小文字数は外から決める
  minChars = 1,
  // 候補が0件のときに出す操作（「新規品目として登録」など）。
  // 自由入力の逃げ道を作らないため、ここに入れるのは**登録ダイアログを開く操作**だけ。
  emptyAction = null,
  emptyText = '該当する登録マスターがありません。',
}) => {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < minChars) { setHits([]); return undefined; }
    setBusy(true);
    timer.current = setTimeout(async () => {
      try {
        const rows = await search(q.trim());
        setHits((rows || []).filter((r) => sources.includes(r.source)
          && (!itemTypes || r.source !== 'mfg' || itemTypes.includes(r.item_type))));
      } catch { setHits([]); } finally { setBusy(false); }
    }, 250);
    return () => { if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const pick = (r) => { onPick?.(r); setQ(''); setHits([]); };

  return (
    <div data-testid={`${testIdPrefix}-picker`}>
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <Label className="text-xs font-semibold text-zinc-700">{label}</Label>
        {statusSlot}
      </div>

      {/* 紐付け中は「何に紐付いているか」を常に見せる。編集の副作用で黙って
          外れてはいけないので、解除は必ず明示操作。 */}
      {linked && (
        <div className="mb-1.5 flex flex-wrap items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5"
          data-testid={`${testIdPrefix}-linked`}>
          <Package size={13} weight="fill" className="shrink-0 text-emerald-700" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium text-emerald-900">{linked.label}</span>
            <span className="flex flex-wrap items-center gap-x-2 text-[11px]">
              {linked.sku
                ? <span className="font-mono font-semibold text-emerald-800" data-testid={`${testIdPrefix}-linked-sku`}>品番 {linked.sku}</span>
                : <span className="text-emerald-700">品番なし</span>}
              {linked.sourceLabel && <span className="text-emerald-700">{linked.sourceLabel}</span>}
            </span>
          </span>
          {onUnlink && (
            <button type="button" onClick={onUnlink}
              className="shrink-0 text-[11px] font-semibold text-emerald-800 underline hover:text-emerald-950"
              data-testid={`${testIdPrefix}-unlink`}>{unlinkLabel}</button>
          )}
        </div>
      )}

      <div className="relative">
        <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => { setQ(e.target.value); onQueryChange?.(e.target.value); }} placeholder={placeholder}
          className="h-10 pl-8" data-testid={`${testIdPrefix}-search`} />
        {busy && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-zinc-400">検索中…</span>}
      </div>

      {hits.length > 0 && (
        <div className="mt-2 max-h-52 divide-y divide-zinc-200 overflow-y-auto rounded-md border border-zinc-300"
          data-testid={`${testIdPrefix}-results`}>
          {hits.map((r) => (
            <button key={`${r.source}-${r.id}`} type="button" onClick={() => pick(r)}
              data-testid={`${testIdPrefix}-option-${r.id}`}
              className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-teal-50/60">
              <Package size={14} className="mt-0.5 shrink-0 text-zinc-500" />
              <span className="min-w-0 flex-1">
                {/* 品番は品名の後ろに繋げず独立して出す。繋げると品名が長いときに
                    truncate で消え、品番で探した人が候補を選び分けられない。 */}
                <span className="block truncate text-sm text-zinc-900">{r.name}</span>
                <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
                  {r.sku
                    ? <span className="font-mono font-semibold text-teal-700" data-testid={`${testIdPrefix}-option-sku-${r.id}`}>品番 {r.sku}</span>
                    : <span className="text-zinc-400">品番なし</span>}
                  {r.model_no && <span className="max-w-[14rem] truncate text-zinc-500">{r.model_no}</span>}
                  {r.drawing_no && <span className="text-zinc-400">図 {r.drawing_no}</span>}
                  {r.maker && <span className="max-w-[10rem] truncate text-zinc-400"><span data-no-translate>{r.maker}</span></span>}
                </span>
              </span>
              <span className="mt-0.5 shrink-0 whitespace-nowrap text-xs text-zinc-600">
                {renderResultExtra ? renderResultExtra(r) : (
                  <>{r.source_label}{r.on_hand != null ? ` · 在庫 ${r.on_hand}` : ''}</>
                )}
              </span>
            </button>
          ))}
        </div>
      )}

      {emptyAction && q.trim().length >= minChars && !busy && hits.length === 0 && (
        <div className="mt-2 rounded-md border border-zinc-300 bg-white px-3 py-2"
          data-testid={`${testIdPrefix}-empty`}>
          <p className="text-[12px] text-zinc-600">{emptyText}</p>
          <div className="mt-2">{emptyAction}</div>
        </div>
      )}

      {helper && <p className="mt-1 text-[11px] leading-snug text-zinc-500">{helper}</p>}
    </div>
  );
};

export default MasterItemPicker;
